import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, CardHeader } from '~/components/ui';
import { supabase } from '~/lib/supabase';

export const Route = createFileRoute('/_app/admin/feedback')({
  component: FeedbackAdmin,
});

type Row = {
  id: string;
  created_at: string;
  source: string;
  variant: string | null;
  given_by: string;
  context: string | null;
  body: string;
  screenshot_data: string | null;
};

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'mobile', label: '📱 Mobile' },
  { key: 'desktop', label: '🖥 Desktop' },
] as const;

function FeedbackAdmin() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<'all' | 'mobile' | 'desktop'>('all');
  const [shotOpen, setShotOpen] = useState<Row | null>(null);

  const { data: rows, isLoading, error } = useQuery({
    queryKey: ['app-feedback'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('app_feedback')
        .select('id, created_at, source, variant, given_by, context, body, screenshot_data')
        .order('created_at', { ascending: false })
        .limit(500);
      if (error) throw error;
      return data as Row[];
    },
  });

  const del = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('app_feedback').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['app-feedback'] }),
  });

  const shown = (rows ?? []).filter((r) => filter === 'all' || r.source === filter);

  return (
    <div className="px-6 py-6 max-w-5xl mx-auto w-full space-y-4">
      <div className="flex items-center gap-4 flex-wrap">
        <h1 className="font-display text-3xl font-bold text-[var(--color-navy)]">Feedback</h1>
        <div className="flex gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className="rounded-full px-3 py-1.5 text-xs font-bold border transition-colors"
              style={filter === f.key
                ? { background: '#032e5b', color: '#fff', borderColor: '#032e5b' }
                : { background: '#fff', color: '#334155', borderColor: '#e2e8f0' }}
            >
              {f.label}
              {rows ? ` (${f.key === 'all' ? rows.length : rows.filter((r) => r.source === f.key).length})` : ''}
            </button>
          ))}
        </div>
      </div>

      {isLoading && <div className="text-sm text-[var(--color-ink-muted)]">Loading feedback…</div>}
      {error && (
        <Card><CardBody>
          <div className="text-sm" style={{ color: '#dc2626' }}>
            Couldn't load feedback: {(error as Error).message}
          </div>
        </CardBody></Card>
      )}
      {rows && shown.length === 0 && (
        <Card><CardBody>
          <div className="text-sm text-[var(--color-ink-muted)]">
            No feedback yet. The ＋ Feedback button on the mobile demo pages and here in the desktop CRM files entries into this list.
          </div>
        </CardBody></Card>
      )}

      {shown.map((r) => (
        <Card key={r.id}>
          <CardHeader>
            <div className="flex items-center gap-2 flex-wrap w-full">
              <span
                className="rounded-full px-2.5 py-0.5 text-[11px] font-extrabold uppercase tracking-wide"
                style={r.source === 'mobile'
                  ? { background: '#eff6ff', color: '#1d4ed8' }
                  : { background: '#f0fdf4', color: '#047857' }}
              >
                {r.source === 'mobile' ? '📱 Mobile' : '🖥 Desktop'}
              </span>
              <span className="text-sm font-bold">{r.given_by}</span>
              <span className="text-xs text-[var(--color-ink-muted)]">
                {new Date(r.created_at).toLocaleString('en-US', {
                  month: 'numeric', day: 'numeric', year: '2-digit', hour: 'numeric', minute: '2-digit',
                })}
                {r.context ? ` · from ${r.context}` : ''}
                {r.variant && r.variant !== r.context ? ` (${r.variant})` : ''}
              </span>
              <button
                onClick={() => { if (confirm('Delete this feedback entry?')) del.mutate(r.id); }}
                className="ml-auto text-xs font-bold rounded px-2 py-1"
                style={{ color: '#dc2626', border: '1px solid #fecaca', background: '#fef2f2' }}
              >
                Delete
              </button>
            </div>
          </CardHeader>
          <CardBody>
            <div className="flex gap-4 items-start">
              <div className="text-sm whitespace-pre-wrap flex-1">{r.body}</div>
              {r.screenshot_data && (
                <button onClick={() => setShotOpen(r)} className="shrink-0" aria-label="Open screenshot">
                  <img
                    src={r.screenshot_data}
                    alt="Feedback screenshot"
                    style={{ width: 110, borderRadius: 8, border: '1px solid #e2e8f0', display: 'block' }}
                  />
                </button>
              )}
            </div>
          </CardBody>
        </Card>
      ))}

      {shotOpen && (
        <div
          onClick={() => setShotOpen(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 95, background: 'rgba(2,8,20,.75)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, cursor: 'zoom-out',
          }}
        >
          <img
            src={shotOpen.screenshot_data!}
            alt="Feedback screenshot"
            style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 12, boxShadow: '0 24px 80px rgba(0,0,0,.5)' }}
          />
        </div>
      )}
    </div>
  );
}
